import { create } from 'zustand';
import type { Academy } from '@court-sync/shared';
import { queryClient } from '../queryClient';
import { queryKeys } from '../queries/keys';
import { onAcademyContextSwitch } from '../queries/academyContextSwitch';
import { fetchOwnerStripeTierQuery } from '../queries/academyOwnerStripeTier';

interface AcademyContextState {
  selectedAcademyId: string | null;
  /** Previous academy id — used to detect context switches. */
  previousAcademyId: string | null;

  selectAcademy: (academy: Academy | null) => void;
  clearAcademyContext: () => void;
}

function seedAcademyCache(academy: Academy): void {
  queryClient.setQueryData(queryKeys.academies.detail(academy.id), academy);
  queryClient.setQueryData<Academy[]>(queryKeys.academies.user(), (old) => {
    if (!old?.length) return [academy];
    const idx = old.findIndex((a) => a.id === academy.id);
    if (idx === -1) return [...old, academy];
    const next = [...old];
    next[idx] = academy;
    return next;
  });
}

export const useAcademyContextStore = create<AcademyContextState>((set, get) => ({
  selectedAcademyId: null,
  previousAcademyId: null,

  selectAcademy: (academy) => {
    const previousAcademyId = get().selectedAcademyId;
    const nextId = academy?.id ?? null;

    if (academy) {
      seedAcademyCache(academy);
    }

    // Same academy — refresh cache only; avoid Zustand/context churn on detail reloads.
    if (previousAcademyId === nextId) {
      return;
    }

    set({ selectedAcademyId: nextId, previousAcademyId: previousAcademyId });

    onAcademyContextSwitch(previousAcademyId, nextId);

    if (academy?.slug) {
      void fetchOwnerStripeTierQuery(academy.slug);
    }
  },

  clearAcademyContext: () => {
    const previousAcademyId = get().selectedAcademyId;
    set({ selectedAcademyId: null, previousAcademyId: null });
    onAcademyContextSwitch(previousAcademyId, null);
  },
}));
